import { env } from 'src/config/env';
import { clearActiveStack, getActiveStack, isPreviewBuild, setActiveStack } from 'src/config/backend';
import { decodeTokenAud, fetchPreviewStack, getLastDiscoveryStatus } from 'src/api/stackManager';
import { getCurrentUserIdToken } from 'src/modules/firebase';
import { clearRevertNotice, probeBackendHealth, setRevertNotice } from 'src/modules/previewStackHealth';
import { queryClient } from 'src/queries/base';
import { updateLocalStorageUserID } from 'src/utils/usersUtils';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { reportError } from 'src/modules/logs/Sentry';

export const PREVIEW_PARAM = 'stack';

const ENV_OVERRIDE_KEY = 'baobab_environment_override';

export const applyPreviewStackFromUrl = async (): Promise<void> => {
  if (!isPreviewBuild()) return;
  const params = new URLSearchParams(window.location.search);
  const slug = params.get(PREVIEW_PARAM);
  if (!slug) return;
  try {
    const info = await fetchPreviewStack(slug);
    setActiveStack({ slug: info.stack_id, apiUrl: info.api_url, chatUrl: info.chat_url });
  } catch (e) {
    reportError('preview stack resolve failed', e);
  } finally {
    params.delete(PREVIEW_PARAM);
    const qs = params.toString();
    // Keep react-router's `{ usr, key, idx }`: an empty state here poisons every later index (see utils/historyIndex).
    window.history.replaceState(window.history.state, '', `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash}`);
  }
};

// Boot gate: if a persisted stack override points at an unhealthy backend, soft-reset it and leave
// a one-shot notice so the badge can explain the fallback.
export const guardActiveStackHealth = async (): Promise<void> => {
  if (!isPreviewBuild()) return;
  const active = getActiveStack();
  if (!active) {
    clearRevertNotice();
    return;
  }
  const healthy = await probeBackendHealth(active.apiUrl);
  if (healthy) {
    clearRevertNotice();
    return;
  }
  clearActiveStack();
  setRevertNotice(active.slug);
  reportError('preview stack unhealthy at boot; reverted to staging', new Error(`stack=${active.slug}`));
};

export type PreviewDiagnostics = {
  managerUrl: string;
  tokenProject: string | null;
  activeSlug: string | null;
  activeApiUrl: string | null;
  lastDiscoveryStatus: number | null;
};

export const hardResetPreview = async (): Promise<void> => {
  clearActiveStack();
  localStorage.removeItem(ENV_OVERRIDE_KEY);
  try {
    await FirebaseAuthentication.signOut();
  } catch (e) {
    reportError('hard reset signOut failed', e);
  }
  updateLocalStorageUserID(null);
  queryClient.clear();
};

export const getPreviewDiagnostics = async (): Promise<PreviewDiagnostics> => {
  const active = getActiveStack();
  let tokenProject: string | null = null;
  try {
    tokenProject = decodeTokenAud(await getCurrentUserIdToken());
  } catch {
    // No signed-in user (or an undecodable token) is an expected state for a diagnostics read —
    // surface it as "(none)" in the readout, not a reportError (which would be Sentry noise on
    // every signed-out open of the panel).
    tokenProject = null;
  }
  return {
    managerUrl: env.stackManagerUrl ?? '',
    tokenProject,
    activeSlug: active?.slug ?? null,
    activeApiUrl: active?.apiUrl ?? null,
    lastDiscoveryStatus: getLastDiscoveryStatus(),
  };
};
